import React from "react";
import { Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export const PROGRESS_OPTIONS = [
  { v: 0, l: "Da iniziare (0%)" },
  { v: 25, l: "In corso 25%" },
  { v: 50, l: "In corso 50%" },
  { v: 75, l: "In corso 75%" },
  { v: 100, l: "Completato 100%" },
];

export const avgProgress = (progress) =>
  progress.length ? Math.round(progress.reduce((a, p) => a + (Number(p.percent) || 0), 0) / progress.length) : 0;

export default function ProgressManager({
  title, enabled, onToggle, isPro, progress, setProgress, manual, setManual,
  onSave, saving, onReminder, className = "",
}) {
  const auto = avgProgress(progress);
  const ov = manual ?? auto;
  const setPct = (i, pct) => {
    setProgress((s) => s.map((p, idx) => (idx === i ? { ...p, percent: pct } : p)));
    setManual(null);
  };
  const setNote = (i, note) => setProgress((s) => s.map((p, idx) => (idx === i ? { ...p, note } : p)));

  return (
    <div data-testid="progress-manager" className={`bg-card border border-slate-200/80 rounded-xl p-6 shadow-sm ${className}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            {title}
            {!isPro && <span data-testid="progress-pro-badge" className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">PRO</span>}
          </h2>
          <p className="text-sm text-slate-500">Aggiorna lo stato per ogni voce: il cliente lo vede in tempo reale sul link pubblico.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Mostra al cliente</span>
          <Switch data-testid="progress-enabled-toggle" checked={!!enabled} onCheckedChange={onToggle} />
        </div>
      </div>
      {isPro ? (
        <>
          <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 p-3">
            <div className="flex justify-between items-center text-sm mb-2 gap-2 flex-wrap">
              <span className="text-slate-500">Avanzamento complessivo {manual == null ? "(media automatica delle voci)" : "(impostato manualmente)"}</span>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" max="100" data-testid="progress-overall-input" value={ov}
                  onChange={(e) => setManual(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  className="w-20 h-8 font-mono text-right" />
                <span className="font-mono font-bold text-primary" data-testid="progress-overall">{ov}%</span>
                {manual != null && (
                  <button type="button" data-testid="progress-overall-reset" onClick={() => setManual(null)} className="text-xs font-semibold text-primary hover:underline">Usa media ({auto}%)</button>
                )}
              </div>
            </div>
            <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${ov}%` }} /></div>
          </div>
          <div className="space-y-3">
            {progress.map((p, i) => (
              <div key={p.item_id || i} data-testid={`progress-row-${i}`} className="rounded-lg border border-border p-3">
                <p className="text-sm font-medium text-slate-800 mb-2">Voce {i + 1}{p.title ? ` · ${p.title}` : ""}</p>
                <div className="grid sm:grid-cols-[200px_1fr] gap-3 items-center">
                  <Select value={String(Number(p.percent) || 0)} onValueChange={(v) => setPct(i, Number(v))}>
                    <SelectTrigger data-testid={`progress-status-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROGRESS_OPTIONS.map((o) => (
                        <SelectItem key={o.v} value={String(o.v)} data-testid={`progress-option-${i}-${o.v}`}>{o.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input data-testid={`progress-note-${i}`} value={p.note || ""} onChange={(e) => setNote(i, e.target.value)} placeholder="Nota di aggiornamento (opzionale)" />
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-2.5">
                  <div className="h-full bg-indigo-500 transition-all" style={{ width: `${Number(p.percent) || 0}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button data-testid="progress-save" onClick={onSave} disabled={saving} className="font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Salva Avanzamento
            </Button>
            {onReminder && (
              <>
                <Button data-testid="reminder-acconto" variant="outline" onClick={() => onReminder("acconto")} className="font-semibold">Invia Sollecito Acconto (50%)</Button>
                <Button data-testid="reminder-saldo" variant="outline" onClick={() => onReminder("saldo")} className="font-semibold">Richiedi Saldo Finale (100%)</Button>
              </>
            )}
          </div>
        </>
      ) : (
        <p className="text-sm text-slate-500" data-testid="progress-upgrade-hint">L'Avanzamento Lavori in tempo reale è incluso nei piani Pro e Fondatore. <a href="/billing" className="text-primary font-semibold hover:underline">Passa a Pro</a></p>
      )}
    </div>
  );
}
